import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import type {
  ChartBlock as ChartBlockType,
  ChartSeries,
} from '@netsuite-ai/types';
import { createPortal } from 'react-dom';
import { Maximize2, X } from 'lucide-react';

const EChart = lazy(() => import('./EchartsCore'));

/**
 * The `dark` class on <html> is the single source of truth (globals.css), and it
 * has TWO writers — theme.store.ts and the NetSuite host bridge. Observing the
 * class catches both; subscribing to the store would miss host-driven changes
 * and OS-level flips while preference is 'system'.
 */
function useIsDark() {
  const [dark, setDark] = useState(() =>
    document.documentElement.classList.contains('dark'),
  );
  useEffect(() => {
    const el = document.documentElement;
    const ob = new MutationObserver(() =>
      setDark(el.classList.contains('dark')),
    );
    ob.observe(el, { attributes: true, attributeFilter: ['class'] });
    return () => ob.disconnect();
  }, []);
  return dark;
}

// Hex-valued vars ONLY. zrender parses hex/rgb/hsl but NOT oklch(), and the dark
// neutral ramp in globals.css is oklch — reading those would break canvas fills.
const SERIES_VARS = [
  '--color-primary',
  '--color-info',
  '--color-amber',
  '--color-success-text',
  '--color-danger',
];

function readTheme() {
  const cs = getComputedStyle(document.documentElement);
  const v = (n: string) => cs.getPropertyValue(n).trim();
  return {
    palette: SERIES_VARS.map(v).filter(Boolean),
    ink: v('--color-ink') || '#161513',
  };
}

// TODO: source currency from the tenant/subsidiary rather than assuming USD.
function fmt(value: unknown, format?: ChartSeries['format']) {
  if (typeof value !== 'number') return String(value ?? '');
  if (format === 'currency')
    return value.toLocaleString(undefined, {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    });
  if (format === 'percent') return `${value.toFixed(1)}%`;
  return value.toLocaleString();
}

/** spec → ECharts option. The entire library-specific surface lives here. */
function buildOption(
  block: ChartBlockType,
  theme: ReturnType<typeof readTheme>,
): Record<string, unknown> {
  const { kind, x, series, stacked, data } = block;
  const valueFormat = series[0]?.format;

  const base = {
    color: theme.palette,
    aria: { enabled: true }, // canvas has no DOM to read
    textStyle: { color: theme.ink, fontFamily: 'inherit' },
    dataset: { dimensions: [x.key, ...series.map((s) => s.key)], source: data },
    tooltip: {
      trigger: kind === 'pie' || kind === 'scatter' ? 'item' : 'axis',
      valueFormatter: (v: unknown) => fmt(v, valueFormat),
    },
    ...(series.length > 1
      ? { legend: { bottom: 0, textStyle: { color: theme.ink } } }
      : {}),
  };

  if (kind === 'pie') {
    return {
      ...base,
      series: [
        {
          type: 'pie',
          radius: ['45%', '70%'],
          encode: { itemName: x.key, value: series[0]!.key },
          label: { color: theme.ink },
        },
      ],
    };
  }

  // Neutral greys as rgba literals — readable on both themes, and parseable.
  const grid = { lineStyle: { color: 'rgba(128,128,128,0.18)' } };

  return {
    ...base,
    grid: {
      left: 8,
      right: 12,
      top: 24,
      bottom: series.length > 1 ? 32 : 8,
      containLabel: true,
    },
    xAxis: {
      type:
        x.type === 'time' ? 'time' : x.type === 'number' ? 'value' : 'category',
      ...(x.label
        ? { name: x.label, nameLocation: 'middle', nameGap: 28 }
        : {}),
      axisLine: { lineStyle: { color: 'rgba(128,128,128,0.35)' } },
      axisLabel: { color: theme.ink },
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: grid,
      axisLabel: {
        color: theme.ink,
        formatter: (v: number) => fmt(v, valueFormat),
      },
    },
    series: series.map((s) => ({
      // 'area' is not an ECharts type — it's a line with an areaStyle.
      type: kind === 'area' ? 'line' : kind,
      name: s.label ?? s.key,
      ...(kind === 'area' ? { areaStyle: {}, smooth: true } : {}),
      ...(stacked ? { stack: 'total' } : {}),
      encode: { x: x.key, y: s.key },
    })),
  };
}

export function ChartBlock({ block }: { block: ChartBlockType }) {
  const isDark = useIsDark();
  const [expanded, setExpanded] = useState(false);
  const option = useMemo(
    () => buildOption(block, readTheme()),
    [block, isDark], // isDark re-reads the CSS vars
  );

  const empty = !block.data?.length || !block.series?.length;

  // Escape closes; body scroll is locked while the overlay owns the viewport.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [expanded]);

  // Rendered at two sizes: 280px inline, 100% inside the overlay's flex column.
  const chart = (height: number | string) =>
    empty ? (
      <p className="px-2 py-8 text-center text-sm text-neutral-500">
        No data to chart.
      </p>
    ) : (
      <Suspense
        fallback={<div className="h-70 animate-pulse rounded bg-neutral-100" />}
      >
        <EChart
          option={option}
          themeKey={isDark ? 'dark' : 'light'}
          height={height}
        />
      </Suspense>
    );

  return (
    <>
      <figure className="relative my-2 w-full overflow-hidden rounded border border-neutral-200 bg-white shadow-sm">
        {block.title && (
          // pr-12 keeps a long title from running under the absolute button.
          <figcaption className="border-b border-neutral-100 px-4 py-3 pr-12 font-semibold text-neutral-900">
            {block.title}
          </figcaption>
        )}
        {!empty && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="text-ink/40 hover:text-ink absolute top-2 right-2 z-10 flex h-8 w-8 items-center justify-center"
            aria-label="Expand chart"
          >
            <Maximize2 size={16} strokeWidth={2} />
          </button>
        )}
        <div className="px-2 py-3">{chart(280)}</div>
      </figure>

      {expanded &&
        createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm sm:p-8"
            onClick={() => setExpanded(false)}
            role="dialog"
            aria-modal="true"
            aria-label={block.title ?? 'Expanded chart'}
          >
            <div
              className="flex h-full max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-2 border-b border-neutral-100 px-4 py-3">
                <span className="font-semibold text-neutral-900">
                  {block.title ?? 'Chart'}
                </span>
                <button
                  type="button"
                  onClick={() => setExpanded(false)}
                  className="text-ink/40 hover:text-ink flex h-8 w-8 shrink-0 items-center justify-center"
                  aria-label="Close expanded chart"
                >
                  <X size={18} strokeWidth={2} />
                </button>
              </div>
              {/* min-h-0 lets this flex child shrink, so height:100% resolves. */}
              <div className="min-h-0 flex-1 px-2 py-3">{chart('100%')}</div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
